"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MapPin, Mail, Phone, ShieldCheck, Sparkles, Globe, Loader, Send, Instagram, Facebook, Pin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-secondary/20 flex items-center justify-center mb-4 border border-secondary/50 shadow-lg shadow-secondary/10">
          <ShieldCheck className="size-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-default font-medium text-foreground">Thank You</h3>
        <p className="text-muted-foreground font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/20 hover:bg-primary/5 rounded-md font-mono tracking-widest uppercase text-xs">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Please enter your name"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono tracking-widest uppercase text-xs">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Evelyn Harper" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
              </FormControl>
              <FormDescription className="font-sans text-[10px] italic">How should we address you?</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Please enter your email",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono tracking-widest uppercase text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="evelyn@example.com" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
              </FormControl>
              <FormDescription className="font-sans text-[10px] italic">For our formal correspondence.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="subject" rules={{
        required: "Please select your event type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono tracking-widest uppercase text-xs">Event Type</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Summer Wedding" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono tracking-widest uppercase text-xs">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-secondary transition-colors px-0" {...field} />
              </FormControl>
              <FormDescription className="font-sans text-[10px] italic">Optional, for a personal call.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please share your vision"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono tracking-widest uppercase text-xs">Your Vision Details</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about your dream day..." className="flex w-full min-w-0 border-0 border-b border-border bg-transparent px-0 py-2 text-base transition-colors outline-none placeholder:text-muted-foreground/50 focus-visible:border-secondary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm min-h-[120px] resize-y font-sans" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/10 rounded-md flex items-start gap-3">
          <div className="text-destructive mt-0.5"></div>
          <p className="text-destructive text-xs font-sans">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] h-12 bg-primary text-primary-foreground rounded-md shadow-md hover:shadow-lg transition-all font-mono tracking-widest uppercase text-xs">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : <>
            <Send className="mr-2 size-4" />
            Begin Your Journey
          </>}
      </Button>
    </Form>;
}
export default function LetSCreateMagic() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="221642" id="lets-create-magic" className="bg-background text-foreground relative overflow-hidden py-24">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-secondary/5 rounded-lg blur-3xl" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[40%] bg-primary/5 rounded-lg blur-3xl" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMTk3LDE2MCw4OSwwLjA1KSIvPjwvc3ZnPg==')] [mask-image:linear-gradient(to_bottom,white,transparent)] opacity-30" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-7xl">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-lg bg-secondary/10 border border-secondary/20 text-secondary mb-6">
            <Sparkles className="size-3" />
            <span className="text-[10px] font-mono font-medium tracking-widest uppercase">Exclusively Yours</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-default font-light mb-6 tracking-tight">
            Let's Create <span className="italic text-secondary">Magic</span>
          </h2>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">
            Every great love story deserves a breathtaking stage. Share your vision with our curators, and let us transform your dreams into an unforgettable reality at VOW.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-start transition-all duration-1000 delay-200 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="lg:col-span-5 space-y-8">
            <div className="relative p-8 rounded-md bg-card/50 border border-border/30 backdrop-blur-sm overflow-hidden group shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <h3 className="text-2xl font-default font-medium mb-8 flex items-center gap-3">
                <Globe className="size-5 text-secondary" />
                The Estate Office
              </h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="mt-1 p-2 rounded-md bg-secondary/5 border border-secondary/10">
                    <MapPin className="size-4 text-secondary" />
                  </div>
                  <div>
                    <h4 className="font-mono text-[10px] tracking-widest uppercase text-foreground mb-1">Location</h4>
                    <p className="text-muted-foreground font-sans text-sm leading-relaxed">
                      1200 Willow Creek Lane<br />
                      Emerald Valley, CA 90210
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-1 p-2 rounded-md bg-secondary/5 border border-secondary/10">
                    <Mail className="size-4 text-secondary" />
                  </div>
                  <div>
                    <h4 className="font-mono text-[10px] tracking-widest uppercase text-foreground mb-1">Inquiries</h4>
                    <p className="text-muted-foreground font-sans text-sm">
                      concierge@vowvenue.com<br />
                      events@vowvenue.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-1 p-2 rounded-md bg-secondary/5 border border-secondary/10">
                    <Phone className="size-4 text-secondary" />
                  </div>
                  <div>
                    <h4 className="font-mono text-[10px] tracking-widest uppercase text-foreground mb-1">Direct Line</h4>
                    <p className="text-muted-foreground font-sans text-sm">
                      Private Tours & Bookings<br />
                      +1 (555) 828-VOWS
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-border/30">
                <h4 className="font-mono text-[10px] tracking-widest uppercase text-foreground mb-4">Follow Our Inspiration</h4>
                <div className="flex gap-4">
                  <Link data-index={0} to="https://instagram.com" newTab className="size-10 rounded-md bg-background border border-border/50 flex items-center justify-center text-muted-foreground hover:text-secondary hover:border-secondary/50 hover:shadow-md transition-all duration-500" aria-label="Instagram">
                    <Instagram className="size-5" />
                  </Link>
                  <Link to="https://facebook.com" newTab className="size-10 rounded-md bg-background border border-border/50 flex items-center justify-center text-muted-foreground hover:text-secondary hover:border-secondary/50 hover:shadow-md transition-all duration-500" aria-label="Facebook">
                    <Facebook className="size-5" />
                  </Link>
                  <Link to="https://pinterest.com" newTab className="size-10 rounded-md bg-background border border-border/50 flex items-center justify-center text-muted-foreground hover:text-secondary hover:border-secondary/50 hover:shadow-md transition-all duration-500" aria-label="Pin">
                    <Pin className="size-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative p-0.5 rounded-md bg-gradient-to-b from-border/30 to-transparent">
              <div className="absolute inset-0 bg-card/20 backdrop-blur-xl rounded-md shadow-xl" />
              <div className="relative bg-card/40 backdrop-blur-md p-8 sm:p-10 rounded-md border border-border/20">
                <div className="mb-8">
                  <h3 className="text-2xl font-default font-medium mb-2">Begin Your Journey</h3>
                  <p className="text-muted-foreground font-sans text-sm">
                    Complete the form below, and our dedicated event team will reach out within 24 hours to discuss your vision.
                  </p>
                </div>

                <FormProvider formKey="magic_form_begin_your_journey" formId="magic_form_begin_your_journey" sectionName="ContactBetnav" defaultValues={{
                name: "",
                email: "",
                subject: "",
                message: "",
                phone: ""
              }} submitText="Begin Your Journey" successMessage="Your inquiry has been received. We look forward to crafting something beautiful together." errorMessage="We encountered an error. Please try again or contact us directly.">
                  <ContactFormInner />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}